import { MutationHookOptions, gql, useMutation } from '@apollo/client';
import { useMemo } from 'react';
import { CreateSavedRowResponse, UpsertSavedRowVariables } from '../types';

export type CreateSavedRowOptions = MutationHookOptions<
  CreateSavedRowResponse,
  UpsertSavedRowVariables
>;

const createSavedRowMutation = gql`
  mutation CreateBeeRow(
    $rowName: String!
    $messageMedium: MessageMedium!
    $html: String!
    $beeMetadata: String!
    $categoryId: Long
    $categoryName: String
  ) {
    createBeeRow(
      name: $rowName
      messageMedium: $messageMedium
      content: { html: $html, beeBuilderMetadata: $beeMetadata }
      category: { existingCategoryId: $categoryId, createCategoryWithName: $categoryName }
    ) {
      beeRowMetadataId
      beeRowName
      failure {
        ... on BeeCreateFieldNotProvided {
          field
        }
        ... on BeeCreateFieldTooLongFailure {
          field
        }
        ... on BeeFieldInvalidFailure {
          field
          errorType {
            ... on AnchorTagsWithJS {
              errorMsg
            }
            ... on HasCycle {
              errorMsg
            }
            ... on HasScriptTag {
              errorMsg
            }
            ... on IframeTagWithJS {
              errorMsg
            }
            ... on IllegalCharacters {
              errorMsg
            }
            ... on NotRenderable {
              errorMsg
            }
          }
        }
        ... on BeeRowNameAlreadyExistsFailure {
          name
        }
        ... on BeeSavedRowCreationFailure {
          errorMsg
        }
        ... on MessageCategoryMaximumCountExceededFailure {
          errorMsg
        }
        ... on MessageCategoryNameTooLongFailure {
          maximumLength
        }
      }
    }
  }
`;

export const useCreateSavedRowMutation = (options?: CreateSavedRowOptions) => {
  const mutationOptions: CreateSavedRowOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      ...options,
    }),
    [options],
  );

  const [createSavedRow, { data, loading, reset }] = useMutation<
    CreateSavedRowResponse,
    UpsertSavedRowVariables
  >(createSavedRowMutation, mutationOptions);

  return {
    failure: data?.createBeeRow?.failure || null,
    isCreatingSavedRow: loading,
    createSavedRow,
    reset,
  };
};
